import { BaseDealSummaryMainContainer } from './BaseDealSummary.style';
import { Grid } from '@interstate/components/Grid';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useBaseDealBreakpoint } from '../../../utils/useBaseDealBreakpoint';
import { useDispatch, useSelector } from 'react-redux';
import * as baseDealSelectors from '@makemydeal/dr-dash-store/selectors/BaseDealSelectors';
import * as dealLifeCycleActionCreators from '@makemydeal/dr-dash-store/actions/dealLifeCycleActionCreators';
import * as dealLifecycleSelectors from '@makemydeal/dr-dash-store/selectors/dealLifecycleSelectors';
import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';
import { dealerSelectors } from '@makemydeal/dr-shared-store';
import type { StateTree } from '@makemydeal/dr-dash-types';
import type { OfferType } from '@makemydeal/dr-platform-types';
import { LEASE, FINANCE } from '@makemydeal/dr-platform-types';
import { useMediaQuery } from 'react-responsive';
import { BreakPoint } from '@makemydeal/dr-activities-common';
import type { ColumnConfig, ColumnConfigProps, SectionField } from './SummaryGrid';
import { SummaryGrid, summaryLeftColumnConfig, summaryRightColumnConfig } from './SummaryGrid';
import { ProfitDetails, useProfitDetailsVisibility } from './ProfitDetails';
import { useSecondPaymentDateField } from './hooks/useSecondPaymentDateField';
import {
    AvgMonthlyPaymentField,
    ComparableMonthField,
    FirstPaymentPeriodField,
    NextPaymentPeriodField,
    SaleDateField,
    useGetFlexBuyCurrentMonths,
    DeliveredDateField,
    formatUtils,
    SALE,
    DELIVERED,
    DealLifeCycleStatus,
    DATE_FORMAT,
    SecondPaymentDueDateField
} from '@makemydeal/dr-dash-components';
import { MANAGER_VIEW_APP_PREFIX } from '@makemydeal/dr-shared-ui-utils';
import { debounce } from 'lodash';
import type { InterstateOnChangeEvent } from '@interstate/components/InterstateEvents';
import type { DatePickerEventValue } from '@interstate/components/DatePicker';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';

const additionalFieldsLeftColumnConfig: Record<OfferType, ColumnConfig> = {
    ...summaryLeftColumnConfig,
    [FINANCE]: {
        ...summaryLeftColumnConfig[FINANCE],
        desktop: 4
    },
    [LEASE]: {
        ...summaryLeftColumnConfig[LEASE],
        desktop: 4
    }
};

const additionalFieldsRightColumnConfig: Record<OfferType, ColumnConfig> = {
    ...summaryRightColumnConfig
};

export const AdditionalFields = () => {
    const isDealCentralExperience = useSelector(dealerSelectors.isDealCentralExperience);
    const currentOfferType = useSelector(offerReduxSelectors.getCurrentOfferType);
    const paymentGridEnabledToggle = useSelector((state: StateTree) =>
        offerReduxSelectors.getPaymentGridToggle(state, currentOfferType)
    );

    const isFlexBuyPayment = useSelector(offerReduxSelectors.isFlexBuyPayment);
    const flexBuyPaymentGridEnabled = useSelector(offerReduxSelectors.getFlexBuyPaymentGridToggle);
    const isPaymentGridEnabled =
        (isFlexBuyPayment ? flexBuyPaymentGridEnabled : paymentGridEnabledToggle) && isDealCentralExperience;

    const breakpoints = useBaseDealBreakpoint({ summaryTab: true, isPaymentGridEnabled });
    const isBreakdownOpen = useSelector(baseDealSelectors.getBreakdownStatus);
    const isDealCentralMultiScenario = useSelector(baseDealSelectors.isDealCentralMultiScenario);
    const isMobile = useMediaQuery({ query: `(max-width: ${BreakPoint.SMALL})` }) || isDealCentralMultiScenario;
    const dealLifeCycle = useSelector(dealLifecycleSelectors.getDealLifecycle);
    const { approvedDate, contractSignedDate } = dealLifeCycle;

    const dispatch = useDispatch();
    const { flexBuyCurrentMonths } = useGetFlexBuyCurrentMonths();
    const showProfitFields = isFlexBuyPayment && !!flexBuyCurrentMonths;
    const isSingleColumnLayout = useMediaQuery({
        query: `(min-width: ${currentOfferType === LEASE ? breakpoints.SMALL : BreakPoint.MEDIUM}) and (max-width: ${
            currentOfferType === LEASE ? breakpoints.MEDIUM : breakpoints.LG
        })`
    });
    const { value: secondPaymentDateValue, onChange: handleSecondPaymentDateChange } = useSecondPaymentDateField();
    const [saleFieldValue, setSaleFieldValue] = useState(dealLifeCycle.soldDate || '');
    const [deliveredFieldValue, setDeliveredFieldValue] = useState(dealLifeCycle.deliveredDate || '');
    const dealStatusValue = dealLifeCycle.status || DealLifeCycleStatus.IN_PROGRESS;
    const DEAL_LIFECYCLE_UPDATE_DEBOUNCE_MS = 1000;
    const enableDealLifeCycleUpdates = false;

    const enableLeaseContractField = useSelector(featureToggleSelectors.isEnableLeaseContractFields);

    const debouncedUpdateDealLifecycleData = useMemo(
        () =>
            debounce((newSaleFieldValue: string, newDeliveredFieldValue: string) => {
                if (dealStatusValue !== DealLifeCycleStatus.SOLD || newSaleFieldValue) {
                    dispatch(
                        dealLifeCycleActionCreators.updateDealLifeCycleData(
                            MANAGER_VIEW_APP_PREFIX,
                            {
                                soldDate: formatUtils.getMidDayUTCTime(newSaleFieldValue, DATE_FORMAT),
                                status: dealStatusValue,
                                approvedDate,
                                deliveredDate: formatUtils.getMidDayUTCTime(newDeliveredFieldValue, DATE_FORMAT),
                                contractSignedDate
                            },
                            enableDealLifeCycleUpdates
                        )
                    );
                }
            }, DEAL_LIFECYCLE_UPDATE_DEBOUNCE_MS),
        [dispatch, dealStatusValue, approvedDate, contractSignedDate]
    );

    const updateDealLifecycleData = useCallback(
        (newSaleFieldValue: string, newDeliveredFieldValue: string) => {
            debouncedUpdateDealLifecycleData(newSaleFieldValue, newDeliveredFieldValue);
        },
        [debouncedUpdateDealLifecycleData]
    );

    const handleChange = useCallback(
        (event: InterstateOnChangeEvent<DatePickerEventValue>, type: string) => {
            const value = event.target.value;
            const newSaleFieldValue = type === SALE ? value.dateValue ?? '' : saleFieldValue;
            const newDeliveredFieldValue = type === DELIVERED ? value.dateValue ?? '' : deliveredFieldValue;

            if (type === SALE) {
                setSaleFieldValue(newSaleFieldValue);
            } else if (type === DELIVERED) {
                setDeliveredFieldValue(newDeliveredFieldValue);
            }

            updateDealLifecycleData(newSaleFieldValue, newDeliveredFieldValue);
        },
        [updateDealLifecycleData, saleFieldValue, deliveredFieldValue]
    );

    useEffect(() => {
        return () => {
            debouncedUpdateDealLifecycleData.cancel();
        };
    }, [debouncedUpdateDealLifecycleData]);

    useEffect(() => {
        if (dealLifeCycle.soldDate !== saleFieldValue) {
            setSaleFieldValue(dealLifeCycle.soldDate || '');
        }
        if (dealLifeCycle.deliveredDate !== deliveredFieldValue) {
            setDeliveredFieldValue(dealLifeCycle.deliveredDate || '');
        }
    }, [dealLifeCycle.soldDate, dealLifeCycle.deliveredDate]);

    const { shown: isProfitDetailsDisplayed } = useProfitDetailsVisibility();

    const leftSectionFields = useMemo(() => {
        const saleDateField: SectionField = {
            component: (
                <SaleDateField
                    id="summary-tab-sale-date-picker"
                    value={saleFieldValue}
                    showStyledLabel={true}
                    onChange={handleChange}
                    dealStatusValue={dealStatusValue}
                    size="sm"
                />
            )
        };
        const deliveryDateField: SectionField = {
            component: (
                <DeliveredDateField
                    id="summary-tab-delivery-date-picker"
                    value={deliveredFieldValue}
                    showStyledLabel={true}
                    onChange={handleChange}
                    dealStatusValue={dealStatusValue}
                    size="sm"
                />
            )
        };
        const secondPaymentField: SectionField = {
            component: (
                <SecondPaymentDueDateField
                    id="summary-tab-second-payment-date-picker"
                    value={secondPaymentDateValue}
                    showStyledLabel={false}
                    onChange={handleSecondPaymentDateChange}
                    size="sm"
                />
            )
        };
        const profitField: SectionField | undefined = isProfitDetailsDisplayed ? { component: <ProfitDetails /> } : undefined;
        const firstPaymentPeriodField: SectionField = { component: <FirstPaymentPeriodField /> };
        const nextPaymentPeriodField: SectionField = { component: <NextPaymentPeriodField /> };
        const avgMonthlyPaymentField: SectionField = { component: <AvgMonthlyPaymentField /> };
        const comparableMonthField: SectionField = { component: <ComparableMonthField /> };
        const profitFields = showProfitFields
            ? [firstPaymentPeriodField, nextPaymentPeriodField, avgMonthlyPaymentField, comparableMonthField]
            : [];
        let dealStatusFields: SectionField[] = [];
        dealStatusFields = [
            saleDateField,
            deliveryDateField,
            ...(enableLeaseContractField && currentOfferType === LEASE ? [secondPaymentField] : [])
        ];
        return profitField ? [profitField, ...profitFields, ...dealStatusFields] : [...profitFields, ...dealStatusFields];
    }, [
        currentOfferType,
        dealLifeCycle.deliveredDate,
        dealLifeCycle.soldDate,
        deliveredFieldValue,
        showProfitFields,
        saleFieldValue,
        dealStatusValue,
        secondPaymentDateValue,
        handleSecondPaymentDateChange,
        handleChange
    ]);

    const columnConfig: ColumnConfigProps = useMemo(() => {
        const rightConfig = additionalFieldsRightColumnConfig[currentOfferType];
        const leftConfig = additionalFieldsLeftColumnConfig[currentOfferType];
        return {
            desktop: rightConfig.desktop + leftConfig.desktop,
            tablet: isSingleColumnLayout ? rightConfig.tablet : rightConfig.tablet + leftConfig.tablet,
            mobile: rightConfig.mobile
        };
    }, [isSingleColumnLayout, currentOfferType]);

    return (
        <BaseDealSummaryMainContainer breakpoints={breakpoints} isBreakdownOpen={isBreakdownOpen}>
            <Grid container padding={`${isMobile ? 0 : 4}`} justifyContent="space-between">
                <SummaryGrid leftSectionFields={leftSectionFields} columnConfig={columnConfig} />
            </Grid>
        </BaseDealSummaryMainContainer>
    );
};
